'use client'
import type { ChainId } from '@morpho-org/blue-sdk'

import { DataList } from '@morpho-org/ui-v6/client'
import { type Address, isAddressEqual } from 'viem'

import { AddressDataLine } from '@/components/common/DataLine'
import { vaultV1PublicAllocatorAddresses } from '@/helpers/addresses'

import type { VaultV1Allocators } from './allocators.contract'

type Allocator = VaultV1Allocators['allocators'][number]

const ALLOCATOR_ROLE_TOOLTIP =
  "Executes liquidity movements within curator-approved constraints. Has no ownership, custody, or control over depositors' assets. Multiple addresses can hold this role."

const isPublicAllocator = (allocator: Allocator, publicAllocator?: Address) =>
  publicAllocator ? isAddressEqual(allocator.address, publicAllocator) : false

const sortAllocators = (allocators: Allocator[], publicAllocator?: Address) =>
  allocators.toSorted((a, b) => {
    if (isPublicAllocator(a, publicAllocator)) return 1
    if (isPublicAllocator(b, publicAllocator)) return -1
    return 0
  })

export interface VaultRiskCurationClientProps {
  allocators: Allocator[]
  /** Set when the list may be missing an allocator. */
  partial: boolean
  chainId: ChainId
}

const VaultRiskCurationClient = ({
  allocators,
  partial,
  chainId
}: VaultRiskCurationClientProps) => {
  const publicAllocator = vaultV1PublicAllocatorAddresses[chainId]
  const sortedAllocators = sortAllocators(allocators, publicAllocator)

  const midpoint = Math.ceil(sortedAllocators.length / 2)
  const leftColumn = sortedAllocators.slice(0, midpoint)
  const rightColumn = sortedAllocators.slice(midpoint)

  return (
    <div className="gap-2xs flex w-[100%] flex-col items-start">
      {allocators.length > 0 && (
        <DataList.Root fullWidth>
          <DataList.Column>
            {leftColumn.map((allocator, i) => (
              <AddressDataLine
                key={`allocator-left-${i}`}
                title={`Allocator ${i + 1}`}
                chainId={chainId}
                address={allocator.address}
                toastMessage="Allocator address copied to clipboard."
                tooltipMessage="Copy allocator address."
                labelTag={
                  isPublicAllocator(allocator, publicAllocator) ? 'Public Allocator' : undefined
                }
                roleTooltip={ALLOCATOR_ROLE_TOOLTIP}
              />
            ))}
          </DataList.Column>

          <DataList.Column>
            {rightColumn.map((allocator, i) => (
              <AddressDataLine
                key={`allocator-right-${i}`}
                title={`Allocator ${i + 1 + midpoint}`}
                chainId={chainId}
                address={allocator.address}
                toastMessage="Allocator address copied to clipboard."
                tooltipMessage="Copy allocator address."
                labelTag={
                  isPublicAllocator(allocator, publicAllocator) ? 'Public Allocator' : undefined
                }
                roleTooltip={ALLOCATOR_ROLE_TOOLTIP}
              />
            ))}
          </DataList.Column>
        </DataList.Root>
      )}
      {partial && (
        <p
          className="text-secondary-foreground text-xs"
          data-testid="vault-risk-curation-allocators-partial"
        >
          Some allocators could not be loaded, so this list may be incomplete.
        </p>
      )}
    </div>
  )
}

export default VaultRiskCurationClient
